import { ReactNode, useState } from "react";
import useAdminSchemas, { ADMIN_SCHEMAS_LIMIT } from "admin/hooks/useAdminSchemas";
import useAdminTableDiff from "admin/hooks/useAdminTableDiff";
import {
  MissingFromOMItem,
  OMTableStatus,
  OrphanedInOMItem,
  StaleInOMItem,
} from "admin/types/interfaces/adminTableDiff";
import { format, parseISO } from "date-fns";
import { CircleAlert, CircleCheck, Clock, Ghost, Info, Loader2 } from "lucide-react";
import { Badge, BadgeProps } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Checkbox } from "~/components/ui/checkbox";
import Combobox from "~/components/ui/combobox";
import { Label } from "~/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover";
import { StatsCard } from "~/components/ui/stats-card";
import { cn } from "~/lib/utils";
import { isApiError } from "~/utils/apiClient";

const formatDate = (dateString: string) => format(parseISO(dateString), "MM/dd/yy h:mmaaa");

/** Per-user personal schemas, one per platform user, should not be diffed/synced here. */
const PERSONAL_SCHEMA_PREFIX = "USER_";

/** Per-dataset dedicated schemas — numerous, so opt-in behind a checkbox. */
const DATASET_SCHEMA_PREFIX = "DATASET_";

/** Snowflake's own metadata schema, never a diff target. */
const SNOWFLAKE_METADATA_SCHEMA = "INFORMATION_SCHEMA";

/** Verbiage for the per-column info popovers. */
const COLUMN_INFO = {
  missingFromOm:
    "Tables in Snowflake that the Manifold platform has no valid registration for: never registered, registered but soft-deleted, or registered with a different case. Users can't find these tables in the catalog.",
  staleInOm:
    "Tables which are registered but may be out of date in the Manifold platform. The staleness may be benign, or may indicate that Manifold's metadata needs updates.",
  inSync: "Tables which are properly registered and up to date. No action needed.",
  orphanedInOm:
    "Tables which are registered and active in the Manifold platform but no longer exist in Snowflake. These still appear in search and dataset listings even though SQL queries against them fail.",
} as const;

const isHiddenSchema = (name: string, showDatasetSchemas: boolean) => {
  const upper = name.toUpperCase();
  return (
    upper === SNOWFLAKE_METADATA_SCHEMA ||
    upper.startsWith(PERSONAL_SCHEMA_PREFIX) ||
    (!showDatasetSchemas && upper.startsWith(DATASET_SCHEMA_PREFIX))
  );
};

const OM_STATUS_LABEL: Record<OMTableStatus, string> = {
  [OMTableStatus.NOT_REGISTERED]: "Not registered",
  [OMTableStatus.SOFT_DELETED]: "Soft deleted",
  [OMTableStatus.CASE_MISMATCH]: "Case mismatch",
};

const OM_STATUS_VARIANT: Record<OMTableStatus, BadgeProps["variant"]> = {
  [OMTableStatus.NOT_REGISTERED]: "destructive",
  [OMTableStatus.SOFT_DELETED]: "warning",
  [OMTableStatus.CASE_MISMATCH]: "info",
};

interface DiffColumnProps {
  title: string;
  /** Shown on demand from the header's info button. */
  info: string;
  count: number;
  emptyText: string;
  children: ReactNode;
}

const DiffColumn = ({ title, info, count, emptyText, children }: DiffColumnProps) => (
  <section
    aria-label={title}
    className="flex min-h-0 flex-col overflow-hidden rounded-lg border bg-background"
  >
    <header className="flex shrink-0 items-center justify-between gap-2 border-b px-4 py-3">
      <div className="flex min-w-0 items-center gap-1">
        <h2 className="truncate text-sm font-semibold text-foreground">{title}</h2>
        {/* A Popover, not a Tooltip: this opens on click (and so works on touch),
            whereas a Radix tooltip dismisses on pointer-down. */}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="size-6 shrink-0 text-muted-foreground"
              aria-label={`About ${title}`}
            >
              <Info className="size-3.5" />
            </Button>
          </PopoverTrigger>
          {/* Borrows the design system's tooltip treatment (`bg-foreground
              text-background`, inverted in both light and dark) so the panel
              reads as an overlay rather than another column card. */}
          <PopoverContent
            align="start"
            className="bg-foreground p-3 text-xs leading-relaxed text-background"
          >
            {info}
          </PopoverContent>
        </Popover>
      </div>
      <Badge variant="muted">{count}</Badge>
    </header>
    {count === 0 ? (
      <p className="px-4 py-8 text-center text-sm text-muted-foreground">{emptyText}</p>
    ) : (
      <ul className="min-h-0 flex-1 divide-y overflow-auto">{children}</ul>
    )}
  </section>
);

/** One row in a diff column: the table name, then whatever metadata the category carries. */
const DiffRow = ({
  name,
  icon,
  children,
}: {
  name: string;
  /** Leading status adornment, for categories where every row shares one state. */
  icon?: ReactNode;
  children?: ReactNode;
}) => (
  <li className="px-4 py-2.5">
    <div className="flex min-w-0 items-center gap-1.5">
      {icon}
      <p className="truncate font-mono text-sm font-medium text-foreground">{name}</p>
    </div>
    {children}
  </li>
);

const MetaLine = ({ children, className }: { children: ReactNode; className?: string }) => (
  <p className={cn("truncate text-xs text-muted-foreground", className)}>{children}</p>
);

const MissingRow = ({ item }: { item: MissingFromOMItem }) => (
  <DiffRow name={item.table_name}>
    <div className="mt-1 flex flex-wrap items-center gap-1.5">
      <Badge variant={OM_STATUS_VARIANT[item.om_status]}>{OM_STATUS_LABEL[item.om_status]}</Badge>
      {item.snowflake_last_altered && (
        <MetaLine>Altered {formatDate(item.snowflake_last_altered)}</MetaLine>
      )}
    </div>
    {/* The OpenMetadata-side fqn is the whole story for a case mismatch, and the
        only way to spot a stray registration for the other statuses. */}
    <MetaLine className="mt-1">{item.fqn}</MetaLine>
  </DiffRow>
);

const StaleRow = ({ item }: { item: StaleInOMItem }) => (
  <DiffRow name={item.table_name}>
    <div className="mt-1 flex flex-wrap items-center gap-1.5">
      <Badge variant="warning">{item.stale_by} behind</Badge>
    </div>
    <MetaLine className="mt-1">Snowflake DDL {formatDate(item.snowflake_last_ddl)}</MetaLine>
    <MetaLine>Manifold platform {formatDate(item.om_updated_at)}</MetaLine>
  </DiffRow>
);

const OrphanedRow = ({ item }: { item: OrphanedInOMItem }) => (
  <DiffRow name={item.table_name}>
    <div className="mt-1 flex flex-wrap items-center gap-1.5">
      {/* Blue, matching the Ghost icon on this category's summary tile. */}
      <Badge variant="info">Orphan</Badge>
      <MetaLine>Manifold platform {formatDate(item.om_updated_at)}</MetaLine>
    </div>
    <MetaLine className="mt-1">{item.fqn}</MetaLine>
  </DiffRow>
);

const TableDiff = () => {
  const [schema, setSchema] = useState<string | null>(null);
  const [showDatasetSchemas, setShowDatasetSchemas] = useState(false);
  const {
    items: schemas,
    metadata: schemasMetadata,
    isLoading: schemasLoading,
  } = useAdminSchemas();
  const { diff, isLoading, error } = useAdminTableDiff(schema);

  const schemaOptions = schemas
    .filter(({ name }) => !isHiddenSchema(name, showDatasetSchemas))
    .map(({ name }) => ({ value: name, label: name }));

  // On hide of the per-dataset schemas, reset the selection if it was a dataset schema.
  const handleShowDatasetSchemasChange = (checked: boolean) => {
    setShowDatasetSchemas(checked);
    if (!checked && schema?.toUpperCase().startsWith(DATASET_SCHEMA_PREFIX)) {
      setSchema(null);
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-6">
      <div className="space-y-1.5">
        {/* Not a <label>: the trigger is a role="combobox" button whose accessible
            name already comes from its own content, so htmlFor has nothing to bind to. */}
        <p className="text-sm font-medium text-foreground">Choose a schema:</p>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
          <div className="w-full max-w-sm">
            <Combobox
              className="w-full"
              options={schemaOptions}
              value={schema}
              onChange={setSchema}
              loading={schemasLoading}
              placeholder="Select a schema"
              searchPlaceholder="Search schemas..."
              emptyText="No schemas found."
            />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="show-dataset-schemas"
              aria-label="Show per-dataset schemas"
              checked={showDatasetSchemas}
              onCheckedChange={(checked) => handleShowDatasetSchemasChange(!!checked)}
            />
            <Label htmlFor="show-dataset-schemas" className="font-normal">
              Show per-dataset schemas
            </Label>
          </div>
        </div>
        {schemasMetadata?.has_more && (
          <p className="text-xs text-muted-foreground">
            Showing the first {ADMIN_SCHEMAS_LIMIT} schemas.
          </p>
        )}
      </div>

      {!schema ? (
        <p className="text-sm text-muted-foreground">
          Pick a schema to compare its Snowflake tables against the Manifold platform.
        </p>
      ) : error ? (
        <p className="text-sm text-destructive">
          {isApiError(error) && error.status === 404
            ? `Schema "${schema}" not found in Snowflake, or schema is not inspectable by the Catalog ingest Snowflake user.`
            : "Error comparing tables against the Manifold platform"}
        </p>
      ) : isLoading || !diff ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>Comparing tables...</span>
        </div>
      ) : (
        <>
          {/* Named so the tile captions, which repeat the column headings
              verbatim, are still addressable on their own. */}
          <section aria-label="Diff summary">
            <div className="grid gap-2 sm:grid-cols-3 xl:grid-cols-5">
              <StatsCard
                className="border bg-muted"
                size="compact"
                title={diff.found_in_snowflake}
                description="Tables in Snowflake"
              />
              <StatsCard
                className="border bg-muted"
                size="compact"
                title={diff.found_in_openmetadata}
                description="Tables in Manifold platform"
              />
              <StatsCard
                className="border bg-muted"
                size="compact"
                title={diff.missing_from_om_count}
                description="Only in Snowflake"
                icon={CircleAlert}
                iconColor="text-destructive"
              />
              <StatsCard
                className="border bg-muted"
                size="compact"
                title={diff.stale_in_om_count}
                description="Updated in Snowflake"
                icon={Clock}
                iconColor="text-yellow-600"
              />
              <StatsCard
                className="border bg-muted"
                size="compact"
                title={diff.orphaned_in_om_count}
                description="Only in Manifold"
                icon={Ghost}
                iconColor="text-blue-600"
              />
            </div>
            <p className="px-4 text-xs text-muted-foreground">
              Checked {formatDate(diff.checked_at)}
            </p>
          </section>

          <div className="grid min-h-0 flex-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            <DiffColumn
              title="Only in Snowflake"
              info={COLUMN_INFO.missingFromOm}
              count={diff.missing_from_om.length}
              emptyText="No missing tables"
            >
              {diff.missing_from_om.map((item) => (
                <MissingRow key={item.fqn} item={item} />
              ))}
            </DiffColumn>

            <DiffColumn
              title="Updated in Snowflake"
              info={COLUMN_INFO.staleInOm}
              count={diff.stale_in_om.length}
              emptyText="No stale tables"
            >
              {diff.stale_in_om.map((item) => (
                <StaleRow key={item.fqn} item={item} />
              ))}
            </DiffColumn>

            <DiffColumn
              title="In Sync"
              info={COLUMN_INFO.inSync}
              count={(diff.in_sync ?? []).length}
              emptyText="No tables in sync"
            >
              {(diff.in_sync ?? []).map((name) => (
                <DiffRow
                  key={name}
                  name={name}
                  icon={
                    <CircleCheck
                      role="img"
                      aria-label="In Sync"
                      className="size-3.5 shrink-0 text-green-600"
                    />
                  }
                />
              ))}
            </DiffColumn>

            <DiffColumn
              title="Only in Manifold"
              info={COLUMN_INFO.orphanedInOm}
              count={diff.orphaned_in_om.length}
              emptyText="No orphaned tables"
            >
              {diff.orphaned_in_om.map((item) => (
                <OrphanedRow key={item.fqn} item={item} />
              ))}
            </DiffColumn>
          </div>
        </>
      )}
    </div>
  );
};

export default TableDiff;
